import type { BeforeSendEvent } from '@vercel/analytics/react';

const CANONICAL_ORIGIN = 'https://www.tcgcalendar.com.au';
const PRODUCTION_HOST = 'www.tcgcalendar.com.au';

const SENSITIVE_ROUTE_PATTERNS = [
  /^\/verify(?:\/|$)/i,
  /^\/reset-password(?:\/|$)/i,
];

const DYNAMIC_ROUTE_PATTERNS: Array<[RegExp, string]> = [
  [/^\/events\/[^/]+\/?$/i, '/events/:slug'],
  [/^\/sets\/[^/]+\/?$/i, '/sets/:slug'],
  [/^\/(pokemon|onepiece|magic|riftbound|lorcana|fleshandblood)-competitions\/[^/]+\/?$/i, '/$1-competitions/:stateCode'],
  [/^\/submit-event\/?$/i, '/submit-event'],
];

const pathnameFrom = (value: string): string | null => {
  try {
    return new URL(value, CANONICAL_ORIGIN).pathname || '/';
  } catch {
    return null;
  }
};
export const isSensitiveAnalyticsPath = (value: string): boolean => {
  const pathname = pathnameFrom(value);
  return Boolean(pathname && SENSITIVE_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname)));
};

/**
 * Returns a query-free, hash-free route template. Dynamic public identifiers
 * are measured by allowlisted product events rather than copied into page URLs.
 */
export const getAnalyticsRoute = (value: string): string | null => {
  const pathname = pathnameFrom(value);
  if (!pathname || isSensitiveAnalyticsPath(pathname)) return null;

  for (const [pattern, replacement] of DYNAMIC_ROUTE_PATTERNS) {
    if (pattern.test(pathname)) return pathname.replace(pattern, replacement);
  }

  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
};

export const isAnalyticsCollectionAllowed = (
  consent: 'unknown' | 'granted' | 'denied',
  hostname: string,
  productionBuild: boolean,
  pathname: string,
): boolean => (
  consent === 'granted'
  && productionBuild
  && hostname.toLowerCase() === PRODUCTION_HOST
  && !isSensitiveAnalyticsPath(pathname)
);

/**
 * Anonymous Vercel page counts are the baseline traffic measure. They are
 * cookie-free, use only query-free route templates and never run on token
 * routes. Optional product interaction events remain consent-gated through
 * isAnalyticsCollectionAllowed above.
 */
export const isPageViewAnalyticsAllowed = (
  hostname: string,
  productionBuild: boolean,
  pathname: string,
): boolean => (
  productionBuild
  && hostname.toLowerCase() === PRODUCTION_HOST
  && !isSensitiveAnalyticsPath(pathname)
);

/** Vercel's final safety boundary: unknown/sensitive URLs are dropped. */
export const sanitizeVercelAnalyticsEvent = (event: BeforeSendEvent): BeforeSendEvent | null => {
  const route = getAnalyticsRoute(event.url);
  if (!route) return null;
  return { ...event, url: `${CANONICAL_ORIGIN}${route}` };
};
